/**
 * Shell `Layout` — the top-level frame every route renders inside.
 *
 * Structure (mobile-first, centred on wider viewports per the brief):
 *
 *   <div class="shell flex min-h-screen flex-col bg-background">
 *     <SkipLink/>                         // first Tab-reachable element
 *     <TopBar/>                           // fixed, logo-only <h1>
 *     <main id="main-content" tabIndex={-1}
 *           class="mx-auto max-w-lg flex-1 pt-top-bar pb-bottom-nav px-4">
 *       {children}
 *     </main>
 *     {!isLoginRoute &&                    // fixed bottom nav; hidden on /login
 *       <PrimaryNav onOpenMenu={openMenu}/>} // 4th "Valikko" slot = menu trigger
 *     <MenuSheet key={menuOpenSession}    // secondary-nav surface;
 *                open={menuOpen}          // keyed on the open-session
 *                onOpenChange={handleMenuOpenChange}/> // counter so a
 *                                            // fresh open remounts; the
 *                                            // Sheet primitive owns the
 *                                            // focus trap + escape +
 *                                            // overlay-dismiss behaviour
 *   </div>
 *
 * Spec refs: FR-001 amended (mobile-first shell, primary nav exposes
 * Koti/Viestit/Omat tiedot per D-048 and 026 US1), FR-003 amended by
 * D-050 (the demo nature of the build is disclosed once on `/login`,
 * not via a persistent global indicator), FR-004 (primary nav, three
 * entries, no more), FR-005 (skip-to-main-content), FR-033 (keyboard
 * reachability), FR-040 (Finnish-first copy), FR-056 (the menu sheet
 * hosts the secondary-nav surface — owned by `MenuSheet`).
 *
 * ## Landmark layering
 *
 * `TopBar` renders a `<header>` with the implicit `banner` landmark.
 * `<main>` / implicit `main`. `PrimaryNav` renders a `<nav>` with an
 * explicit Finnish `aria-label="Päänavigaatio"`. The `MenuSheet`'s
 * content owns its own `<nav aria-label="Sivuston toiminnot">` so
 * assistive tech can distinguish the site-actions nav from the primary
 * nav.
 *
 * `<main id="main-content" tabIndex={-1}>` — the `id` is the SkipLink's
 * hash target, and `tabIndex={-1}` makes `<main>` programmatically
 * focusable so the browser sets keyboard focus there on hash-jump.
 *
 * ## Hide-on-login
 *
 * `useLocation()` returns the active pathname; the `/login` frame shows
 * top bar + main, but no bottom nav. This keeps the sign-in surface
 * visually clean and matches the prototype posture. The main content's
 * `pb-bottom-nav` padding is also dropped on `/login` because there is
 * no bottom nav to clear.
 *
 * ## Menu sheet wiring
 *
 * Layout owns both the `menuOpen` boolean and the `menuOpenSession`
 * open-session counter because the trigger lives in `PrimaryNav` (the 4th
 * "Valikko" bottom-nav slot, rendered here) and the sheet body lives in
 * `MenuSheet` (also rendered here). The pair are siblings so the
 * controlled-state flows are wired on both ends here. `PrimaryNav`'s
 * `onOpenMenu` calls `openMenu()`, which sets `menuOpen=true` AND bumps
 * the open-session counter in the same handler. `MenuSheet`'s controlled
 * `onOpenChange` is routed through
 * `handleMenuOpenChange`: a transition to `true` delegates to
 * `openMenu()` (counter-bumping open path); a transition to `false`
 * (Escape, overlay click, in-sheet activation) calls `setMenuOpen(false)`
 * only — the counter is left untouched so the exit animation plays.
 *
 * The `key` on `MenuSheet` is an **open-session counter**, not the
 * `menuOpen` boolean. The counter increments only on the closed→open
 * transition (in the same user-action path that sets `menuOpen=true` —
 * `PrimaryNav`'s `onOpenMenu` and the `onOpenChange` transition to
 * `true`), never on close. Keying on the boolean would remount on CLOSE too,
 * unmounting the tree before Radix `react-presence` can reach
 * `data-state="closed"` — the close keyframes in `shell.css` would never
 * play and the sheet would snap out instantly (the regression this
 * counter fixes; `shell.css:59` documents that BOTH directions must
 * animate). With the counter, closing keeps the same instance mounted so
 * Radix animates the exit; the next open bumps the counter and remounts a
 * fresh `MenuSheet`, so its submenu `useState` initialiser is still the
 * "every drawer open starts with both submenus collapsed" reset
 * (B-INV-001, R10). The increment is effect-free — it happens in the open
 * handler, NOT in a reset `useEffect` (R11; the prop-sync reset Effect
 * was the anti-pattern this idiom replaces).
 *
 * ## Desktop shell (058)
 *
 * From `DESKTOP_MIN_WIDTH_PX` (`useViewport`) the chrome is swapped, not
 * hidden: `DesktopSidebar` and `DesktopLanguageSwitcher` mount in place of
 * `TopBar`, `PrimaryNav` and `MenuSheet`, which leave the DOM and the
 * accessibility tree. `/login` keeps today's frame at every width. The desktop
 * offsets are `lg:` utilities, which flip at the same width
 * (`desktopBreakpoint.test.ts`), so the mobile classes are unchanged.
 *
 * Crossing into desktop width closes an open menu sheet; otherwise crossing
 * back would reopen it. The close is applied while rendering (the previous
 * width is kept in state), not in an Effect, for the same reason the
 * open-session counter is. `ComposeWizardProvider` stays outermost, so an
 * open wizard survives a crossing in either direction.
 *
 * Every utility class below is layout (`flex`, `gap-*`, `px-*`, `py-*`)
 * or a token-backed colour utility. No raw hex, no `*-[…]` brackets.
 */

import type { JSX, ReactNode } from 'react';
import { useState } from 'react';
import { useLocation } from 'react-router';

import { AnalyticsConsentNotice } from '../analytics/AnalyticsConsentNotice.js';
import { MemberBanner } from '../content/MemberBanner.js';
import { ComposeWizardProvider } from '../member-messaging/compose/wizard/ComposeWizardProvider.js';

import { DesktopLanguageSwitcher } from './DesktopLanguageSwitcher.js';
import { DesktopSidebar } from './DesktopSidebar.js';
import { FloatingFeedbackTab } from './FloatingFeedbackTab.js';
import { MenuSheet } from './MenuSheet.js';
import { PrimaryNav } from './PrimaryNav.js';
import { SkipLink } from './SkipLink.js';
import { showsFloatingFeedback } from './showsFloatingFeedback.js';
import { TopBar } from './TopBar.js';
import { useViewport } from './useViewport.js';

const shellClasses = 'shell flex min-h-screen flex-col bg-background';

// max-w-lg (512px) app-wide content column (item-4 r1.1 C8, owner live-review):
// widened from max-w-md (448px); affects every member screen under the shell.
const mainBaseClasses = 'mx-auto flex w-full max-w-lg flex-1 flex-col px-4 pt-top-bar';
const mainWithNavClasses = 'pb-bottom-nav';
// Desktop shell: content beside the sidebar, in the prototype's 760 px column,
// still centred by `mx-auto` and still under `pt-top-bar` (the back header
// keeps its vertical position).
const shellDesktopClasses = 'lg:pl-desktop-sidebar';
const mainDesktopClasses = 'lg:max-w-content-column';
// Omat tiedot and a single message keep the prototype's wider columns, which
// the customer chose over one shared width (#734).
const mainDesktopProfileClasses = 'lg:max-w-content-column-profile';
const mainDesktopMessageClasses = 'lg:max-w-content-column-message';
const singleMessagePath = /^\/messages\/[^/]+\/?$/u;

function desktopColumnClasses(pathname: string): string {
  if (pathname === '/profile') return mainDesktopProfileClasses;
  if (singleMessagePath.test(pathname)) return mainDesktopMessageClasses;
  return mainDesktopClasses;
}
// flex-1 lets the content column fill the main landmark's height so a route that
// opts in (a `flex-1` section, e.g. the Omat tiedot sheet) can stretch to the
// bottom nav. Routes that don't opt in keep content-height, top-aligned — the
// extra height is empty and invisible.
const mainContentClasses = 'flex flex-1 flex-col gap-4 py-4';

function classes(...values: readonly string[]): string {
  return values.filter((v) => v.length > 0).join(' ');
}

export interface LayoutProps {
  /**
   * Routed content rendered inside the `<main>` landmark. Omitted by
   * landmark-only tests — an absent `children` renders an empty `<main>`.
   */
  readonly children?: ReactNode;
}

export function Layout({ children }: LayoutProps): JSX.Element {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuReturnFocusTo, setMenuReturnFocusTo] = useState<HTMLElement | null>(null);
  // Open-session counter. Bumped only on the closed→open transition so
  // the `MenuSheet` key changes on open (fresh instance → B-INV-001
  // reset) but NOT on close (same instance stays mounted so Radix
  // react-presence can animate `data-state="closed"`). Effect-free —
  // incremented in the user-action open path, never in a `useEffect`.
  const [menuOpenSession, setMenuOpenSession] = useState(0);

  // Single open-intent path: opens the sheet AND bumps the session
  // counter in the same handler (the `PrimaryNav` "Valikko" click). The
  // functional updater keeps the increment correct under React 18+
  // batching / StrictMode double-invocation.
  const openMenu = (trigger?: HTMLElement): void => {
    if (trigger !== undefined) setMenuReturnFocusTo(trigger);
    setMenuOpen(true);
    setMenuOpenSession((session) => session + 1);
  };

  // Radix-driven controlled change. A transition to `true` (should not
  // happen today — the only opener is the Valikko trigger — but kept
  // correct if a future Radix trigger opens the sheet) routes through the same
  // counter-bumping path; any transition to `false` (Escape, overlay
  // click, in-sheet activation) just flips the boolean and leaves the
  // counter untouched so the exit animation plays.
  const handleMenuOpenChange = (next: boolean): void => {
    if (next) {
      openMenu();
      return;
    }
    setMenuOpen(false);
  };

  const isLoginRoute = location.pathname === '/login';
  const { isDesktop } = useViewport();
  const isDesktopShell = isDesktop && !isLoginRoute;

  // Close the sheet on the crossing into desktop width (see the header).
  const [wasDesktop, setWasDesktop] = useState(isDesktop);
  if (wasDesktop !== isDesktop) {
    setWasDesktop(isDesktop);
    if (isDesktop) setMenuOpen(false);
  }

  return (
    // ComposeWizardProvider: the single "Ota yhteyttä" wizard mount (027 US6 /
    // D-120, FR-023). Layout hosts it so every member surface under the shell
    // chrome — the MenuSheet action row, the Messages FAB, the Home
    // quick-access, the content-surface CTAs — opens the SAME modal.
    <ComposeWizardProvider>
      <div
        className={classes(shellClasses, isDesktopShell ? shellDesktopClasses : '')}
        data-feedback={showsFloatingFeedback(location.pathname) || undefined}
      >
        <SkipLink />
        {isDesktopShell ? (
          <>
            <DesktopSidebar />
            <DesktopLanguageSwitcher />
          </>
        ) : (
          <TopBar />
        )}
        {/* The in-shell lapsed "limited access" banner (NonMemberNotice) was
          REMOVED in 042 (D-202, binary access): an ended membership is refused a
          member session at sign-in and never enters the shell, so there is no
          in-shell lapsed state left to announce. A non-active person lands on the
          standalone `NoMembershipBlock` terminal route at `/ei-jasenyytta`
          instead. */}
        <main
          id="main-content"
          tabIndex={-1}
          className={classes(
            mainBaseClasses,
            isLoginRoute || isDesktop ? '' : mainWithNavClasses,
            isDesktopShell ? desktopColumnClasses(location.pathname) : '',
          )}
        >
          {/* The sitewide banner strip (US14) is IN FLOW under the TopBar and pushes
              the page content down, as the proto renders it; the shell mount renders
              only sitewide banners and skips "/" where Home mounts its own strip. */}
          <MemberBanner placement="shell" />
          <AnalyticsConsentNotice />
          <div className={mainContentClasses}>{children}</div>
        </main>
        {showsFloatingFeedback(location.pathname) && <FloatingFeedbackTab />}
        {!isDesktop && !isLoginRoute && <PrimaryNav onOpenMenu={openMenu} />}

        {!isDesktop && (
          <MenuSheet
            key={menuOpenSession}
            open={menuOpen}
            onOpenChange={handleMenuOpenChange}
            returnFocusTo={menuReturnFocusTo}
          />
        )}
      </div>
    </ComposeWizardProvider>
  );
}
